import { AtlassianProductHostingType } from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';
import { useLocation } from '@atlassian/mpac-ui';
import { useEffect } from 'react';

import {
  toAnalyticsHosting,
  toAnalyticsPaymentModel,
} from './app/components/Routes/components/AppListingRoutes/components/AppsRoute/utils';
import * as aceAnalytics from './common/utils/analytics/aceAnalytics';
import {
  addVendorGAId,
  contentGroupCompatibleProduct,
  contentGroupHosting,
  contentGroupPaymentModel,
  removeVendorGAId,
  trackPageView as trackGAPageView,
} from './common/utils/analytics/analytics';

import type { AppVersion } from '@atlassian/mpac-app-listing';

const getFormattedCategory = (s: string) => {
  // words should be separated by underscore,
  // no special characters
  return s
    .split(/\W/)
    .filter((token) => token.length !== 0)
    .join('_');
};

const getPageTitleFromAppName = (appName: string) => `${appName} | Atlassian Marketplace`;

type AppListingPageAnalyticsProps = {
  appName: string;
  appId: string;
};

export const useAppListingPageAnalytics = ({ appName, appId }: AppListingPageAnalyticsProps) => {
  const { pathname } = useLocation();
  const pageTitle = getPageTitleFromAppName(appName);
  useEffect(() => {
    // @ts-expect-error [MC-2850] - TS2554 - Expected 2 arguments, but got 1.
    trackGAPageView(pathname);
    // TODO: Add a referrer here after App Listing page is a part of the SPA flow
    aceAnalytics.trackPageView(document.referrer, 'appListingScreen', {
      // Usually, ace analytics would populate page_title and category for us, but
      // since we're firing this lazily as opposed to on page load, we'll have
      // to do this ourselves.
      category: getFormattedCategory(pageTitle),
      poweredBy: 'AMKT_FRONTEND',
      page_title: pageTitle,
    });
  }, [appId]);
};

type VendorGAEventProps = {
  appId: string;
  appKey: string;
  appName: string;
  googleAnalyticsId: string | null | undefined;
  buildNumber: string;
};

export const useVendorGoogleAnalytics = ({
  appId,
  appName,
  appKey,
  googleAnalyticsId,
  buildNumber,
}: VendorGAEventProps) => {
  useEffect(() => {
    if (googleAnalyticsId) {
      addVendorGAId(
        googleAnalyticsId,
        appId,
        appKey,
        buildNumber,
        getPageTitleFromAppName(appName)
      );
    }
    return () => {
      removeVendorGAId(googleAnalyticsId, appId);
    };
  }, [googleAnalyticsId, appId]);
};

type GoogleAnalyticsExtraContextProps = {
  cloudAppVersion: AppVersion | null | undefined;
  serverAppVersion: AppVersion | null | undefined;
  dataCenterAppVersion: AppVersion | null | undefined;
};

export const useGoogleAnalyticsExtraContext = ({
  cloudAppVersion,
  serverAppVersion,
  dataCenterAppVersion,
}: GoogleAnalyticsExtraContextProps) => {
  const getContentGroups = () => {
    const hostingOptions: Array<any> = [];
    const paymentModels: Array<any> = [];
    const compatibleProducts: Array<any> = [];

    [cloudAppVersion, serverAppVersion, dataCenterAppVersion].forEach((appVersion) => {
      if (!appVersion?.deployment.compatibleProducts.length) return;

      paymentModels.push(toAnalyticsPaymentModel(appVersion.paymentModel));
      appVersion.deployment.compatibleProducts.forEach((product) => {
        compatibleProducts.push(product.name);
      });
    });

    if (serverAppVersion) {
      hostingOptions.push(toAnalyticsHosting(AtlassianProductHostingType.Server));
    }

    if (cloudAppVersion) {
      hostingOptions.push(toAnalyticsHosting(AtlassianProductHostingType.Cloud));
    }

    if (serverAppVersion) {
      hostingOptions.push(toAnalyticsHosting(AtlassianProductHostingType.DataCenter));
    }

    return { hostingOptions, paymentModels, compatibleProducts };
  };

  useEffect(() => {
    const { hostingOptions, paymentModels, compatibleProducts } = getContentGroups();
    contentGroupHosting(hostingOptions.join(','));
    contentGroupPaymentModel([...new Set(paymentModels)].join(','));
    contentGroupCompatibleProduct([...new Set(compatibleProducts)].join(','));

    return () => {
      contentGroupHosting(null);
      contentGroupPaymentModel(null);
      contentGroupCompatibleProduct(null);
    };
  }, [cloudAppVersion, serverAppVersion, dataCenterAppVersion]);
};
